import PropTypes from 'prop-types'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CTA_CTE, MERCADO_PAGO } from '../../constants/mediosEnviosPagos.js'
import { MENSAJE_TIPO_PRESUPUESTO } from '../../constants/mensajesTipos.js'
import {
  ID_PRESUPUESTO_CONFIRMADO,
  ID_PRESUPUESTO_PENDIENTE,
  MAX_CARACTERES_DESCRIPCION,
  MAX_DIAS_ENTREGA
} from '../../constants/presupuesto.js'
import { formatearFecha, esFechaAnteriorAHoy } from '../../helpers/dateValidation'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth.js'
import { setModal } from '../../slices/modalSlice.js'
import { TitledBox } from '../../ui/components/index.js'
import { useDeshabilitaCompra } from '../hooks/deshabilitaCompra.js'
import { useTieneCuentaMPVinculada } from '../hooks/useMercadoPago.js'
import { Checkbox } from '../reutilizables/checkbox/Checkbox.jsx'
import { GenericModal } from '../reutilizables/modal/GenericModal.jsx'
import Precios from '../reutilizables/precios/Precios'
import { Upload } from '../reutilizables/upload/Upload'
import * as mensajesService from '../servicios/mensajesService'
import * as presupuestosItemService from '../servicios/presupuestosItemService'
import * as presupuestosService from '../servicios/presupuestosService'
import * as publicacionesService from '../servicios/publicacionesService'
import Input from '../ui/Input'
import styles from './PresupuestoForm.module.css'
import PresupuestoFormItemList from './PresupuestoFormItemList'

const MODAL_ID_CONFIRMAR = 'confirmar-presupuesto'
const MODAL_ID_ITEM = 'agregar-item-presupuesto'

const PresupuestoForm = ({
  idPresupuesto,
  presupuesto: presupuestoProp,
  idUsuario,
  handleEstado,
  puedeAdministrar,
  esVendedor,
  onGuardar,
  abrirAdjunto,
  nombreAdjunto,
  adjuntoPendiente,
  subiendoAdjunto,
  onCargaAdjunto,
  onEliminarAdjunto,
  mediosPagoBase,
  setMediosPagosBase,
  onAvisoLimiteCompra
}) => {
  const {
    loginDataContext: { aceptaNoHayDatosFiscales, existeDatosFiscales } = {},
    esPerfilAdmin
  } = useAuth()

  const dispatch = useDispatch()
  const { values: modalValues, modalId: activeModalId } = useSelector((state) => state.genericModal)
  const [presupuesto, setPresupuesto] = useState({
    idPresupuesto: presupuestoProp.idPresupuesto,
    descripcion: presupuestoProp.descripcion || '',
    fechaValidez: formatearFecha(presupuestoProp.fechaValidez, 'YYYY-MM-DD'),
    idEstado: presupuestoProp.idEstado,
    anticipo: presupuestoProp.anticipo || 0,
    pagoAnticipo: presupuestoProp.pagoAnticipo,
    cantDiasEntrega: presupuestoProp.cantDiasEntrega ?? ''
  })
  const [presupuestoItems, setPresupuestoItems] = useState([])
  const [formErrors, setFormErrors] = useState({})
  const [unidades, setUnidades] = useState([])
  const [monedas, setMonedas] = useState([])
  const mediosPagoOriginalRef = useRef(null)
  const descripcionRef = useRef(null)
  const mediosPagoRef = useRef(null)
  const fechasRef = useRef(null)
  const anticipoRef = useRef(null)
  const confirmarRef = useRef(null)

  useEffect(() => {
    if (mediosPagoOriginalRef.current === null && mediosPagoBase.length > 0) {
      mediosPagoOriginalRef.current = mediosPagoBase.map((medio) => ({
        idMedioPago: medio.idMedioPago,
        tienePresupuesto: medio.tienePresupuesto
      }))
    }
  }, [mediosPagoBase])

  const { tieneCuentaVinculadaMP } = useTieneCuentaMPVinculada(presupuestoProp?.empidVendedor)
  const { ctaCteBloqueada, validarTotalContraLimites } = useDeshabilitaCompra({
    ...presupuestoProp,
    empid: presupuestoProp?.empidVendedor,
    mediosPagos: mediosPagoBase.filter((medio) => medio.tienePresupuesto)
  })

  const mediosPago = useMemo(
    () =>
      mediosPagoBase.map((medio) => {
        const esMercadoPagoDeshabilitado =
          medio.idMedioPago === MERCADO_PAGO && !tieneCuentaVinculadaMP
        const esCtaCteDeshabilitada = medio.idMedioPago === CTA_CTE && ctaCteBloqueada

        return {
          ...medio,
          habilitado: !esMercadoPagoDeshabilitado && !esCtaCteDeshabilitada,
          tooltip: esMercadoPagoDeshabilitado
            ? 'El vendedor no tiene una cuenta de Mercado Pago vinculada.'
            : esCtaCteDeshabilitada
              ? 'Este presupuesto solo permite cuenta corriente y tu usuario no la tiene habilitada con el vendedor o no es proveedor propio.'
              : ''
        }
      }),
    [mediosPagoBase, tieneCuentaVinculadaMP, ctaCteBloqueada]
  )

  const { fechaValidez, descripcion, anticipo, cantDiasEntrega } = presupuesto
  const camposBase = { isFullWidth: false, isEditable: true, isGrid: false }

  const obtenerDatos = async () => {
    const monedasRtn = await publicacionesService.buscarMoneda()
    setMonedas(monedasRtn.data)

    const unidadesRtn = await publicacionesService.buscarUnidad()
    setUnidades(unidadesRtn.data)
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    obtenerDatos()
  }, [])

  const refsPorCampo = {
    descripcion: descripcionRef,
    mediosPago: mediosPagoRef,
    cantDiasEntrega: fechasRef,
    fechaValidez: fechasRef,
    anticipo: anticipoRef,
    confirmar: confirmarRef
  }

  const mostrarErrores = (errores) => {
    setFormErrors(errores)
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const primerCampoConError = Object.keys(errores).find(
          (campo) => refsPorCampo[campo]?.current
        )
        refsPorCampo[primerCampoConError]?.current?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'center'
        })
      })
    )
  }

  const validacionBase = () => {
    const errores = {}
    if (descripcion.length > MAX_CARACTERES_DESCRIPCION) {
      errores.descripcion = `La descripción no puede superar los ${MAX_CARACTERES_DESCRIPCION} caracteres.`
    }

    if (isNaN(anticipo) || anticipo < 0 || anticipo > 100) {
      errores.anticipo = 'El anticipo debe ser un valor entre 0 y 100.'
    }

    if (cantDiasEntrega < 0 || cantDiasEntrega % 1 !== 0 || cantDiasEntrega > MAX_DIAS_ENTREGA) {
      errores.cantDiasEntrega = `El plazo de entrega debe ser un número entero entre 1 y ${MAX_DIAS_ENTREGA}.`
    }

    if (esFechaAnteriorAHoy(fechaValidez)) {
      errores.fechaValidez = 'La fecha de validez no puede ser una fecha pasada.'
    }
    return errores
  }

  const validarObligatoriedad = () => {
    const errores = {}

    if (!descripcion || !descripcion.trim()) {
      errores.descripcion = 'La descripción no puede estar vacía.'
    }
    if (!mediosPago.some((m) => m.tienePresupuesto)) {
      errores.mediosPago = 'Debés seleccionar al menos un medio de pago.'
    }

    if (presupuesto.confirmar && itemsVisibles.length === 0) {
      errores.confirmar = 'No podés confirmar un presupuesto sin items.'
    }

    if (cantDiasEntrega === '' || cantDiasEntrega === null || cantDiasEntrega === undefined) {
      errores.cantDiasEntrega = 'El plazo de entrega es obligatorio.'
    }

    if (!fechaValidez) {
      errores.fechaValidez = 'La validez del presupuesto es obligatoria.'
    }

    return errores
  }

  const validarFormulario = (confirma) => {
    const erroresBase = validacionBase()
    if (confirma) {
      const erroresObligatoriedad = validarObligatoriedad()
      return { ...erroresBase, ...erroresObligatoriedad }
    }
    return erroresBase
  }

  const itemsVisibles = presupuestoItems.filter((item) => !item._marcadoEliminar)
  const totalNeto = itemsVisibles.reduce((acc, item) => acc + Number(item.total), 0)
  const totalIva = itemsVisibles.reduce(
    (acc, item) => acc + Number(item.total) * ((Number(item.alicuota) || 0) / 100),
    0
  )

  const avisoLimiteCompra = esVendedor ? '' : validarTotalContraLimites(totalNeto + totalIva)

  useEffect(() => {
    onAvisoLimiteCompra?.(avisoLimiteCompra)
  }, [avisoLimiteCompra, onAvisoLimiteCompra])

  const mediosPagoCambiados = mediosPagoBase.filter((medio) => {
    const original = mediosPagoOriginalRef.current?.find((m) => m.idMedioPago === medio.idMedioPago)
    return original && original.tienePresupuesto !== medio.tienePresupuesto
  })

  const hayCambios =
    presupuestoProp.idPresupuesto &&
    (descripcion !== presupuestoProp.descripcion ||
      String(cantDiasEntrega) !== String(presupuestoProp.cantDiasEntrega) ||
      fechaValidez !== formatearFecha(presupuestoProp.fechaValidez, 'YYYY-MM-DD') ||
      presupuestoItems.some((item) => item._pendiente || item._marcadoEliminar) ||
      anticipo !== presupuestoProp.anticipo ||
      Boolean(presupuesto.confirmar) ||
      mediosPagoCambiados.length > 0)

  const obtenerItems = async (idPresupuesto) => {
    const { data: presupuestoItemTemp } =
      await presupuestosItemService.buscarItemPorId(idPresupuesto)
    setPresupuestoItems(presupuestoItemTemp)
  }

  useEffect(() => {
    if (presupuestoProp.idPresupuesto) {
      setPresupuesto({
        idPresupuesto: presupuestoProp.idPresupuesto,
        descripcion: presupuestoProp.descripcion || '',
        fechaValidez: formatearFecha(presupuestoProp.fechaValidez, 'YYYY-MM-DD'),
        idEstado: presupuestoProp.idEstado,
        anticipo: presupuestoProp.anticipo || 0,
        pagoAnticipo: presupuestoProp.pagoAnticipo,
        cantDiasEntrega: presupuestoProp.cantDiasEntrega ?? ''
      })
      obtenerItems(idPresupuesto)
    }
  }, [presupuestoProp])

  const construirCamposFormularioPresupuestoItem = (monedas, unidades) => {
    return [
      {
        ...camposBase,
        type: 'textarea',
        name: 'descripcion',
        label: 'Descripción Item',
        isFullWidth: true,
        rows: 3,
        maxLength: MAX_CARACTERES_DESCRIPCION
      },
      {
        ...camposBase,
        type: 'number',
        name: 'cantidad',
        label: 'Cantidad'
      },
      {
        ...camposBase,
        type: 'number',
        name: 'precio',
        label: 'Precio',
        placeholder: '$0.00'
      },
      {
        ...camposBase,
        type: 'number',
        name: 'alicuota',
        label: '% Alícuota IVA',
        min: 0,
        max: 100
      },
      {
        ...camposBase,
        type: 'select',
        name: 'moneda',
        label: 'Moneda',
        placeholder: 'Seleccionar',
        options: [
          ...monedas.map((moneda) => ({
            value: moneda.idMoneda,
            label: `${moneda.nombre} (${moneda.simbolo.trim()})`,
            key: moneda.idMoneda
          }))
        ]
      },
      {
        ...camposBase,
        type: 'select',
        name: 'uMedida',
        label: 'Unidad Medida',
        placeholder: 'Seleccionar',
        options: [
          ...unidades.map((unidad) => ({
            value: unidad.idUnidadMedida,
            label: unidad.nombre,
            key: unidad.idUnidadMedida
          }))
        ]
      }
    ]
  }

  const abrirModalItem = () => {
    const valoresInicialesFormularioPresupuestoItem = {
      descripcion: '',
      cantidad: '',
      moneda: '',
      uMedida: '',
      precio: '',
      alicuota: 0
    }

    dispatch(
      setModal({
        show: true,
        size: 'lg',
        title: 'Agregar Item',
        type: 'form',
        modalId: MODAL_ID_ITEM,
        editMode: true,
        errores: {},
        values: valoresInicialesFormularioPresupuestoItem,
        campos: construirCamposFormularioPresupuestoItem(monedas, unidades)
      })
    )
  }

  const handleChangeItem = (e) => {
    const { name, value } = e.target
    dispatch(setModal({ values: { ...modalValues, [name]: value } }))
  }

  const validarCamposPresupuestoItem = (values) => {
    const errores = {}

    if (!values.descripcion || !values.descripcion.trim()) {
      errores.descripcion = 'La descripción no puede estar vacía'
    } else if (values.descripcion.length > MAX_CARACTERES_DESCRIPCION) {
      errores.descripcion = `La descripción no puede superar los ${MAX_CARACTERES_DESCRIPCION} caracteres.`
    } else if (
      presupuestoItems.some(
        (item) => item.descripcion.trim().toLowerCase() === values.descripcion.trim().toLowerCase()
      )
    ) {
      errores.descripcion = 'Ya existe un item con la misma descripción'
    }

    if (!values.moneda) {
      errores.moneda = 'Debe seleccionar una moneda'
    }
    if (!values.uMedida) {
      errores.uMedida = 'Debe seleccionar una unidad de medida'
    }
    if (!(parseFloat(values.precio) > 0)) {
      errores.precio = 'El precio debe ser mayor que 0'
    }
    if (!(parseFloat(values.cantidad) > 0)) {
      errores.cantidad = 'La cantidad debe ser mayor que 0'
    }

    return errores
  }

  const handleConfirmItem = () => {
    const errores = validarCamposPresupuestoItem(modalValues)
    if (Object.keys(errores).length > 0) {
      dispatch(setModal({ errores }))
      return false
    }
    const cantidad = parseFloat(modalValues.cantidad)
    const precio = parseFloat(modalValues.precio)
    const alicuota = parseFloat(modalValues.alicuota) || 0
    const uMedida = parseInt(modalValues.uMedida)
    const monedaSeleccionada = monedas.find(
      (moneda) => moneda.idMoneda === parseInt(modalValues.moneda)
    )
    const unidadSeleccionada = unidades.find((unidad) => unidad.idUnidadMedida === uMedida)
    handleAgregarItem({
      descripcion: modalValues.descripcion,
      cantidad,
      moneda: modalValues.moneda,
      uMedida,
      precio,
      total: cantidad * precio,
      alicuota,
      monedaSimbolo: monedaSeleccionada?.simbolo?.trim() ?? modalValues.moneda,
      unidadMedida: unidadSeleccionada?.nombre ?? String(uMedida),
      _pendiente: true
    })
    return true
  }

  const handleAgregarItem = (item) => {
    const tempId = `temp_${Date.now()}_${Math.random()}`
    setPresupuestoItems((prev) => [...prev, { ...item, idPresupuestoItem: tempId }])
  }

  const guardarItemsPendientes = async () => {
    const itemsAEliminar = presupuestoItems.filter(
      (item) => item._marcadoEliminar && typeof item.idPresupuestoItem === 'number'
    )
    for (const item of itemsAEliminar) {
      await presupuestosItemService.eliminar({
        idPresupuesto: presupuestoProp.idPresupuesto,
        idPresupuestoItem: item.idPresupuestoItem
      })
    }

    const itemsPendientes = presupuestoItems.filter(
      (item) => item._pendiente && !item._marcadoEliminar
    )
    for (const item of itemsPendientes) {
      await presupuestosItemService.insertar({
        idPresupuesto: presupuestoProp.idPresupuesto,
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        moneda: parseInt(item.moneda),
        uMedida: item.uMedida,
        precio: item.precio,
        total: item.total,
        alicuota: item.alicuota
      })
    }

    if (itemsAEliminar.length > 0 || itemsPendientes.length > 0) {
      await obtenerItems(idPresupuesto)
    }
  }

  const deleteItemList = (idPresupuestoItem) => {
    setPresupuestoItems((prev) => {
      const item = prev.find((i) => i.idPresupuestoItem === idPresupuestoItem)
      if (item?._pendiente) {
        return prev.filter((i) => i.idPresupuestoItem !== idPresupuestoItem)
      }
      return prev.map((i) =>
        i.idPresupuestoItem === idPresupuestoItem ? { ...i, _marcadoEliminar: true } : i
      )
    })
  }

  const cancelarEliminarItem = (idPresupuestoItem) => {
    setPresupuestoItems((prev) =>
      prev.map((i) =>
        i.idPresupuestoItem === idPresupuestoItem ? { ...i, _marcadoEliminar: false } : i
      )
    )
  }

  const onChange = (e) => {
    const { name, value, type } = e.target
    const parsedValue = type === 'number' ? (value === '' ? '' : Number(value)) : value
    setPresupuesto({ ...presupuesto, [name]: parsedValue })
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const onBlur = (e) => {
    // Evita que el anticipo quede vacío
    if (e.target.name === 'anticipo' && e.target.value === '') {
      setPresupuesto({ ...presupuesto, anticipo: 0 })
    }
  }

  const guardarPresupuesto = async () => {
    try {
      const confirma = Boolean(presupuesto.confirmar)

      const presupuestoTemp = {
        idPresupuesto: presupuestoProp.idPresupuesto,
        descripcion,
        cantDiasEntrega: Number(presupuesto.cantDiasEntrega),
        fechaValidez,
        idEstado: confirma ? ID_PRESUPUESTO_CONFIRMADO : ID_PRESUPUESTO_PENDIENTE,
        anticipo: Number(anticipo) || 0,
        pagoAnticipo: (Number(anticipo) || 0) > 0 ? false : presupuestoProp.pagoAnticipo,
        mediosPago: mediosPagoBase
          .filter((medio) => medio.tienePresupuesto)
          .map((medio) => medio.idMedioPago)
          .join(',')
      }
      await guardarItemsPendientes()
      await presupuestosService.actualizar(presupuestoTemp)
      mediosPagoOriginalRef.current = mediosPagoBase.map((medio) => ({
        idMedioPago: medio.idMedioPago,
        tienePresupuesto: medio.tienePresupuesto
      }))

      if (confirma) {
        const mensaje = {
          idMensajePadre: null,
          idPublicacion: null,
          idPedidoItem: null,
          idPresupuesto: presupuestoProp.idPresupuesto,
          idUsuarioChat: presupuestoProp.idUsuario,
          idUsuarioEmisor: idUsuario,
          idComprador: presupuestoProp.idUsuario,
          idVendedor: idUsuario,
          texto: '',
          privado: false,
          idMensajeTipo: MENSAJE_TIPO_PRESUPUESTO
        }
        await mensajesService.insertar(mensaje)
        handleEstado('Confirmado')
      }

      setPresupuesto((prev) => ({ ...prev, idEstado: presupuestoTemp.idEstado }))
      await onGuardar()
      toast.success('La información fué actualizada.')
      return true
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al guardar el presupuesto.', 'presupuestos')
      return false
    }
  }

  const onSave = async () => {
    const errores = validarFormulario(presupuesto.confirmar)
    if (Object.keys(errores).length > 0) {
      mostrarErrores(errores)
      return
    }
    setFormErrors({})

    if (presupuesto.confirmar) {
      dispatch(
        setModal({
          show: true,
          size: 'sm',
          title: 'Confirmar Presupuesto',
          type: 'botones',
          modalId: MODAL_ID_CONFIRMAR,
          body: 'Estás por confirmar tu presupuesto. Esta acción no se podrá deshacer y le avisaremos a tu comprador que el presupuesto ya está disponible para su pago. ¿Estás seguro?'
        })
      )
      return
    }

    await guardarPresupuesto()
  }

  const onMedioChange = (e) => {
    const idMedio = parseInt(e.target.value)
    const checked = e.target.checked
    if (formErrors.mediosPago) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next.mediosPago
        return next
      })
    }

    setMediosPagosBase((prev) =>
      prev.map((medio) =>
        medio.idMedioPago === idMedio ? { ...medio, tienePresupuesto: checked } : medio
      )
    )
  }

  const onChangeConfirmar = (e) => {
    setPresupuesto({ ...presupuesto, confirmar: e.target.checked })
  }

  return (
    <>
      {esVendedor && (
        <form>
          <div className={styles.flexRow}>
            <TitledBox ref={descripcionRef} titulo="Descripción" error={formErrors.descripcion}>
              <div className={styles.textareaLabelRow}>
                <span className={styles.textareaLabel}>
                  Máximo {MAX_CARACTERES_DESCRIPCION} caracteres
                </span>
                <span
                  className={`${styles.textareaContador} ${
                    descripcion.length > MAX_CARACTERES_DESCRIPCION
                      ? styles.textareaContadorError
                      : ''
                  }`}
                >
                  {descripcion.length}/{MAX_CARACTERES_DESCRIPCION}
                </span>
              </div>
              <textarea
                id="descripcion"
                name="descripcion"
                className={styles.descripcionArea}
                rows="4"
                placeholder="Escribí aquí­ la descripción de tu presupuesto."
                value={descripcion}
                onChange={onChange}
                required
                disabled={!puedeAdministrar}
              ></textarea>
            </TitledBox>
            <TitledBox ref={mediosPagoRef} titulo="Medios de pago" error={formErrors.mediosPago}>
              {mediosPago
                .filter((medioPago) => puedeAdministrar || medioPago.tienePresupuesto)
                .map((medioPago) => {
                  const esMercadoPago = medioPago.idMedioPago === MERCADO_PAGO
                  return (
                    <div key={medioPago.idMedioPago} className={styles.medioPagoFila}>
                      <input
                        type="checkbox"
                        name={'mediopago'}
                        value={medioPago.idMedioPago}
                        onChange={onMedioChange}
                        checked={Boolean(medioPago.tienePresupuesto)}
                        disabled={
                          !puedeAdministrar ||
                          (!medioPago.habilitado && esMercadoPago) ||
                          presupuesto.idEstado !== ID_PRESUPUESTO_PENDIENTE
                        }
                      />
                      <div className={styles.medioPagoNombre}>
                        <i className={medioPago.icono} aria-hidden="true" />
                        {medioPago.nombre}
                        {esMercadoPago && medioPago.descripcion && (
                          <small className="warningMessage">{`(${medioPago.descripcion})`}</small>
                        )}
                        {!medioPago.habilitado && esMercadoPago && (
                          <small className="warningMessage">
                            <i className={`fa fa-exclamation-triangle ${styles.warningIcon}`} />{' '}
                            Para seleccionar este medio de pago,{' '}
                            {esPerfilAdmin ? (
                              <>
                                tenés que vincular una cuenta de Mercado Pago en tu{' '}
                                <Link to="/Perfil">perfil</Link>.
                              </>
                            ) : (
                              'tenés que pedirle al Administrador de tu empresa que vincule una cuenta de Mercado Pago en su perfil.'
                            )}
                          </small>
                        )}
                      </div>
                    </div>
                  )
                })}
            </TitledBox>
            <TitledBox
              ref={fechasRef}
              titulo="Fechas"
              error={formErrors.cantDiasEntrega || formErrors.fechaValidez}
            >
              <div className={styles.detalleCampo}>
                <label htmlFor="cantDiasEntrega" className={styles.detalleLabel}>
                  Plazo de Entrega (días)
                </label>
                <Input
                  name="cantDiasEntrega"
                  type="number"
                  value={cantDiasEntrega}
                  onChange={onChange}
                  min={1}
                  step={1}
                  required
                  disabled={!puedeAdministrar}
                  className={styles.fechaInput}
                />
              </div>
              <div className={styles.detalleCampo}>
                <label htmlFor="fechaValidez" className={styles.detalleLabel}>
                  Validez Presupuesto
                </label>
                <Input
                  name="fechaValidez"
                  type="date"
                  value={fechaValidez}
                  min={formatearFecha(new Date(), 'YYYY-MM-DD')}
                  onChange={onChange}
                  required
                  disabled={!puedeAdministrar}
                  className={styles.fechaInput}
                />
              </div>
            </TitledBox>
            {(puedeAdministrar || (nombreAdjunto && nombreAdjunto.trim() !== '')) && (
              <TitledBox titulo="Adjunto">
                <div className={styles.adjuntoDiv}>
                  {puedeAdministrar && (
                    <Upload
                      idDoc={Number(idPresupuesto || presupuestoProp.idPresupuesto || 0)}
                      idPerfil={presupuestoProp.idVendedor || idUsuario}
                      idTipo={'9'}
                      onCarga={onCargaAdjunto}
                      adjuntoPendiente={adjuntoPendiente ? [adjuntoPendiente] : []}
                      maxFiles={1}
                    />
                  )}
                  {subiendoAdjunto && (
                    <span className={styles.uploadHint}>Subiendo adjunto...</span>
                  )}
                  {(adjuntoPendiente?.nombre || nombreAdjunto)?.trim() && !subiendoAdjunto ? (
                    <div className={styles.adjuntoPreview}>
                      {adjuntoPendiente?.url ? (
                        <a
                          href={adjuntoPendiente.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.archivo}
                          title={adjuntoPendiente.nombre || nombreAdjunto}
                        >
                          {adjuntoPendiente.nombre || nombreAdjunto}
                        </a>
                      ) : (
                        <span
                          className={styles.archivo}
                          onClick={abrirAdjunto}
                          title={adjuntoPendiente?.nombre || nombreAdjunto}
                        >
                          {adjuntoPendiente?.nombre || nombreAdjunto}
                        </span>
                      )}
                      {puedeAdministrar && (
                        <button
                          type="button"
                          className={styles.removeAdjuntoBtn}
                          onClick={onEliminarAdjunto}
                          title="Eliminar adjunto"
                          aria-label="Eliminar adjunto"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      )}
                    </div>
                  ) : null}
                  {puedeAdministrar && nombreAdjunto !== null && nombreAdjunto.trim() === '' && (
                    <span>No se ha cargado ningún archivo adjunto para este presupuesto.</span>
                  )}
                </div>
              </TitledBox>
            )}
            <div
              className={
                puedeAdministrar && presupuesto.idEstado === ID_PRESUPUESTO_PENDIENTE
                  ? styles.flexColumn
                  : styles.flexColumnSolo
              }
            >
              <TitledBox ref={anticipoRef} titulo="Anticipo" error={formErrors.anticipo}>
                <div className={styles.adjuntoDiv}>
                  <Input
                    name="anticipo"
                    type="number"
                    value={anticipo || 0}
                    min={0}
                    max={100}
                    step={1}
                    onChange={onChange}
                    onBlur={onBlur}
                    required
                    disabled={!puedeAdministrar}
                    className={styles.fechaInput}
                  />
                </div>
              </TitledBox>
              {puedeAdministrar && presupuesto.idEstado === ID_PRESUPUESTO_PENDIENTE && (
                <TitledBox ref={confirmarRef} titulo="Confirmación" error={formErrors.confirmar}>
                  <div className={styles.confirmacionBloque}>
                    <div className={styles.checkboxRow}>
                      <Checkbox
                        name="confirmar"
                        checked={Boolean(presupuesto.confirmar)}
                        onChange={onChangeConfirmar}
                        disabled={!puedeAdministrar}
                      />
                      <label>Confirmar presupuesto.</label>
                    </div>
                    <small className="warningMessage">
                      Una vez confirmado, no se podrá modificar y se le avisará al comprador que ya
                      está disponible para su pago.
                    </small>
                  </div>
                </TitledBox>
              )}
            </div>
          </div>
        </form>
      )}
      {presupuestoItems.length > 0 && (
        <div className={styles.detalleTabla}>
          <div className={`${styles.itemFila} ${styles.itemFilaHead}`}>
            <div className={styles.itemCelda}>Item</div>
            <div className={`${styles.itemCelda} ${styles.itemCeldaCentro}`}>Cantidad</div>
            <div className={`${styles.itemCelda} ${styles.itemCeldaDerecha}`}>Precio</div>
            <div className={styles.itemCelda}>Unidad Medida</div>
            <div className={`${styles.itemCelda} ${styles.itemCeldaDerecha}`}>Total</div>
            <div className={`${styles.itemCelda} ${styles.itemCeldaDerecha}`}>% IVA</div>
            <div className={styles.itemCelda}></div>
          </div>
          {presupuestoItems.map((item) => (
            <PresupuestoFormItemList
              key={item.idPresupuestoItem}
              item={item}
              idEstado={presupuestoProp.idEstado}
              onDelete={deleteItemList}
              onCancelarEliminar={cancelarEliminarItem}
              puedeAdministrar={puedeAdministrar}
            />
          ))}
        </div>
      )}
      {itemsVisibles.length > 0 && (
        <div className={styles.totalesPanel}>
          <Precios
            className={styles.precios}
            nombre="Subtotal sin IVA"
            precio={totalNeto}
            monedaSimbolo="$"
          />
          <Precios className={styles.precios} nombre="IVA" precio={totalIva} monedaSimbolo="$" />
          <div className={styles.totalesTotal}>
            <Precios
              className={styles.precios}
              classNameValor={styles.totalesTotalValor}
              nombre="Total a pagar"
              precio={totalNeto + totalIva}
              monedaSimbolo="$"
            />
          </div>

          {avisoLimiteCompra && (
            <p className={styles.avisoLimiteCompra} role="alert">
              {avisoLimiteCompra}
            </p>
          )}
        </div>
      )}
      {puedeAdministrar && presupuesto.idEstado === ID_PRESUPUESTO_PENDIENTE && (
        <>
          <div className={styles.botones}>
            <div className={styles.confirmarFila}>
              <div className={styles.detalleTablaCabecera}>
                {puedeAdministrar && presupuesto.idEstado === ID_PRESUPUESTO_PENDIENTE && (
                  <button
                    type="button"
                    className={`${styles.btnPrincipal} ${styles.btnAgregarItem}`}
                    onClick={abrirModalItem}
                  >
                    + Agregar Item
                  </button>
                )}
              </div>
              <div className={styles.botonGuardarWrapper}>
                <button
                  type="button"
                  className={styles.btnPrincipal}
                  disabled={(!aceptaNoHayDatosFiscales && !existeDatosFiscales) || !hayCambios}
                  onClick={onSave}
                >
                  Guardar Presupuesto
                </button>
                {!aceptaNoHayDatosFiscales && !existeDatosFiscales && (
                  <span className={styles.tooltipDatosFiscales}>
                    Complete sus datos fiscales para continuar
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <GenericModal
        modalId={activeModalId}
        onChange={handleChangeItem}
        onConfirm={activeModalId === MODAL_ID_CONFIRMAR ? guardarPresupuesto : handleConfirmItem}
      />
    </>
  )
}

PresupuestoForm.propTypes = {
  idPresupuesto: PropTypes.number.isRequired,
  presupuesto: PropTypes.shape({
    idPresupuesto: PropTypes.number.isRequired,
    descripcion: PropTypes.string.isRequired,
    cantDiasEntrega: PropTypes.number,
    fechaValidez: PropTypes.string.isRequired,
    idEstado: PropTypes.number.isRequired,
    idPublicacion: PropTypes.number,
    idVendedor: PropTypes.number,
    idUsuario: PropTypes.number,
    anticipo: PropTypes.number,
    pagoAnticipo: PropTypes.bool,
    empidVendedor: PropTypes.string
  }).isRequired,
  idUsuario: PropTypes.number.isRequired,
  handleEstado: PropTypes.func.isRequired,
  puedeAdministrar: PropTypes.bool.isRequired,
  esVendedor: PropTypes.bool,
  onGuardar: PropTypes.func,
  abrirAdjunto: PropTypes.func.isRequired,
  nombreAdjunto: PropTypes.string,
  adjuntoPendiente: PropTypes.shape({
    idAdjunto: PropTypes.number,
    nombre: PropTypes.string,
    url: PropTypes.string,
    contentType: PropTypes.string
  }),
  subiendoAdjunto: PropTypes.bool,
  onCargaAdjunto: PropTypes.func,
  onEliminarAdjunto: PropTypes.func,
  mediosPagoBase: PropTypes.arrayOf(
    PropTypes.shape({
      idMedioPago: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
      icono: PropTypes.string,
      tienePresupuesto: PropTypes.bool.isRequired,
      habilitado: PropTypes.bool.isRequired,
      tooltip: PropTypes.string
    })
  ).isRequired,
  setMediosPagosBase: PropTypes.func.isRequired,
  onAvisoLimiteCompra: PropTypes.func
}

export default PresupuestoForm
